import getMatchingVariant from '../utils/variants/getMatchingVariant';
import updatePrice from '../utils/variants/updatePrice';
import getSellingPlanPrice from '../utils/product/getSellingPlanPrice';
import updateFormControls from '../utils/variants/updateFormControls';
import updateURL from '../utils/variants/updateURL';
import preselectVariantFromURL from '../utils/variants/preselectVariantFromURL';
import updatePerTabletPrice from '../utils/variants/updatePerTabletPrice';

const initProductVariants = (container = document.querySelector('[data-product-main]')) => {
  const variantEls = container.querySelectorAll('[data-variant-input]');
  const sellingPlanEls = container.querySelectorAll('[data-selling-plan-radio]');
  if (!variantEls.length && !sellingPlanEls.length) return;

  const form = container.querySelector('[data-product-form]');
  if (!form) return;

  const elements = {
    variantsDataEl: form.querySelector('[data-product-variants-json]'),
    optionNamesEl: form.querySelector('[data-product-options-json]'),
    variantIdInput: form.querySelector('[data-atc-id-input]'),
    sellingPlanGroupContainerEl: form.querySelector('[data-selling-plan-groups]'),
    priceContainerEls: document.querySelectorAll('[data-product-price-container]'),
    productPerPriceEls: document.querySelectorAll('[data-product-per-price]'),
    atcButton: form.querySelector('button[type="submit"]'),
    variantsDropdownTriggers: form.querySelectorAll('[data-dropdown-trigger]'),
    subscriptionLabelPriceEl: form.querySelector('[data-subscription-price]'),
    subscriptionInput: form.querySelector('[data-selling-plan-subscription]'),
    subscriptionFrequencyEls: form.querySelectorAll('[data-subscription-list] input'),
    subscriptionFrequencyTrigger: form.querySelector('[data-subscription-frequency]'),
    subscriptionFrequencySelectedEl: form.querySelector('[data-subscription-frequency-selected]'),
    subscriptionSaveEl: form.querySelector('[data-subscription-saving]'),
    subscriptionOtpEl: form.querySelector('[data-selling-plan-otp]')
  };

  const sellingPlanIdInput = elements.sellingPlanGroupContainerEl?.querySelector('[data-selling-plan-input]');

  let variantsData = [];
  let optionNames = [];

  try {
    variantsData = JSON.parse(elements.variantsDataEl?.textContent || '[]');
    optionNames = JSON.parse(elements.optionNamesEl?.textContent || '[]');
  } catch (e) {
    console.error('Invalid variant/options JSON', e);
    return;
  }

  const selectedOptions = {};

  const updateVariantDetails = () => {
    const variant = getMatchingVariant(selectedOptions, variantsData, optionNames);
    if (!variant) return;

    if (elements.variantIdInput) {
      elements.variantIdInput.value = variant.id;
    }

    updatePrice({
      priceContainerEls: elements.priceContainerEls,
      price: getSellingPlanPrice(variant, sellingPlanIdInput?.value, 'price'),
      compareAtPrice: getSellingPlanPrice(variant, sellingPlanIdInput?.value, 'compare_at_price'),
      productPerPriceEls: elements.productPerPriceEls,
      variant,
      subscriptionLabelPriceEl: elements.subscriptionLabelPriceEl,
      sellingPlanEls,
      variantsData: variantsData
    });

    updateURL({ variantID: variant.id, sellingPlanIdInput });

    updateFormControls({
      variant,
      variantsData,
      optionNames,
      selectedOptions,
      atcButton: elements.atcButton,
      sellingPlanId: sellingPlanIdInput?.value,
      getSellingPlanPrice
    });
  };

  const updateSelectedOption = (name, value) => {
    selectedOptions[name] = value;
    updateVariantDetails();
  };

  const handleDropdownChange = event => {
    const target = event.target;
    const variantSelectItem = target.closest('[data-variant-select-item]');
    const selectedVariantHTML = target.labels[0].innerHTML.trim();
    const selectedLabel = variantSelectItem?.querySelector('[data-variants-selected-option]');
    elements.variantsDropdownTriggers.forEach(trigger => (trigger.checked = false));
    if (selectedLabel) selectedLabel.innerHTML = selectedVariantHTML;
  };

  const setupVariantInputs = () => {
    variantEls.forEach(variant => {
      variant.addEventListener('change', ({ target }) => {
        if(variant.hasAttribute('data-strength-option')) {
          updatePerTabletPrice(target.value, variantsData, elements.productPerPriceEls);
        }
        updateSelectedOption(target.name, target.value);
      });
      variant.addEventListener('click', handleDropdownChange);
    });
  };

  const setupDropdownTriggers = () => {
    if (elements.variantsDropdownTriggers.length > 0) {
      document.addEventListener('click', () => {
        elements.variantsDropdownTriggers.forEach(dropdown => {
          if (dropdown.checked) dropdown.nextElementSibling.click();
        });
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          elements.variantsDropdownTriggers.forEach(trigger => (trigger.checked = false));
        }
      });
    }
  };

  const handleSubscriptionChange = () => {
    if (!elements.subscriptionInput.checked) return;

    const selectedInput = Array.from(elements.subscriptionFrequencyEls).find(i => i.checked);
    const inputToSelect = selectedInput || elements.subscriptionFrequencyEls[0];

    if (inputToSelect) {
      inputToSelect.click();
      inputToSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const setupSubscriptionInputs = () => {
    if (elements.subscriptionInput) {
      elements.subscriptionInput.addEventListener('change', handleSubscriptionChange);
    }
  };

  const setupSellingPlanInputs = () => {
    sellingPlanEls.forEach((input, index) => {
      input.addEventListener('change', ({ target }) => {
        if (sellingPlanIdInput) {
          sellingPlanIdInput.value = target.value;
          updateVariantDetails();
        }
      });

      if (input.hasAttribute('data-selling-plan-frequency')) {
        input.nextElementSibling?.addEventListener('click', evt => {
          const target = evt.target;
          if (elements.subscriptionFrequencyTrigger.checked) {
            elements.subscriptionFrequencyTrigger.checked = false;
          }
          elements.subscriptionFrequencySelectedEl.textContent = target.textContent;
          elements.subscriptionSaveEl.textContent = target.dataset.sellingPlanAdjustment ? `${target.dataset.sellingPlanAdjustment}` : '';
        });
        if (index == 0 && input.nextElementSibling && elements.subscriptionOtpEl === null) input.nextElementSibling.click();
      }
    });

    if (elements.subscriptionFrequencyTrigger) {
      elements.subscriptionFrequencyTrigger.addEventListener('click', () => {
        elements.subscriptionInput.checked = true;
        elements.subscriptionInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  };

  setupVariantInputs();
  setupDropdownTriggers();
  setupSubscriptionInputs();
  setupSellingPlanInputs();

  preselectVariantFromURL({
    variantsData,
    optionNames,
    form,
    selectedOptions,
    sellingPlanIdInput,
    updateSelectedOption,
    updateVariantDetails,
    variantsDropdownTriggers: elements.variantsDropdownTriggers
  });

  document.addEventListener('click', () => {
    if (elements.subscriptionFrequencyTrigger && elements.subscriptionFrequencyTrigger.checked) {
      elements.subscriptionFrequencyTrigger.nextElementSibling.click();
    }
  });
};

export default initProductVariants;
